import type {NormalizeForDiff} from '_logics/code-highlight';

export type DiffLine = {
  type: 'add' | 'delete' | 'same';
  content: string;
  beforeContent?: string;
  afterContent?: string;
  beforeLineNum?: number;
  afterLineNum?: number;
  /** 基本正規化では一致するが、実行時I/O状態が異なる行か。 */
  volatileStateChanged?: boolean;
};

export type ComputeLineDiffOptions = {
  /** 実行時に変化する値を差分ハイライトの対象に含める。行の対応付けは変えない。 */
  includeVolatileState?: boolean;
  /**
   * 先頭から連続してこの正規表現に一致する行を、比較では変更として扱わない（表示する原文は変えない）。
   * 分類ルールの「先頭の除外行」（API の `normalized_text`）と同じく、各行に `search` 相当で照合する。
   * 両側の除外行は上から順に対応付け、数が違って余った行は片側だけの変更なし行として返す。
   */
  ignoreLeadingLinePattern?: RegExp;
};

/** 先頭の除外行の比較用の値。本文の行とは一致しない */
const IGNORED_LEADING_LINE = '\u0000pghistory-ignored-leading-line\u0000';

/** 先頭から連続して pattern に一致する行数（行末の CR は除いて照合する） */
const countLeadingMatches = (lines: string[], pattern: RegExp | undefined): number => {
  if (!pattern) return 0;
  // g / y フラグの lastIndex に判定が左右されないよう、状態を持たない複製で照合する
  const matcher = new RegExp(pattern.source, pattern.flags.replace(/[gy]/g, ''));
  let count = 0;
  while (count < lines.length && matcher.test(lines[count].replace(/\r$/, ''))) count++;
  return count;
};

const maskLeading = (lines: string[] | null, count: number): string[] | null =>
  lines && count
    ? lines.map((line, index) => (index < count ? IGNORED_LEADING_LINE : line))
    : lines;

type DiffOperation =
  | {type: 'same'; beforeIndex: number; afterIndex: number}
  | {type: 'delete'; beforeIndex: number}
  | {type: 'add'; afterIndex: number};

const getFrontierValue = (frontier: Map<number, number>, diagonal: number): number =>
  frontier.get(diagonal) ?? 0;

/**
 * Myersの最短編集スクリプトを後ろからたどり、行のインデックス付き操作へ変換する。
 */
const backtrack = (
  trace: Map<number, number>[],
  beforeLines: string[],
  afterLines: string[]
): DiffOperation[] => {
  const result: DiffOperation[] = [];
  let beforeIndex = beforeLines.length;
  let afterIndex = afterLines.length;

  for (let editDistance = trace.length - 1; editDistance > 0; editDistance--) {
    const diagonal = beforeIndex - afterIndex;
    const previousFrontier = trace[editDistance - 1];
    const previousDiagonal =
      diagonal === -editDistance ||
      (diagonal !== editDistance &&
        getFrontierValue(previousFrontier, diagonal - 1) <
          getFrontierValue(previousFrontier, diagonal + 1))
        ? diagonal + 1
        : diagonal - 1;
    const previousBeforeIndex = getFrontierValue(previousFrontier, previousDiagonal);
    const previousAfterIndex = previousBeforeIndex - previousDiagonal;

    while (beforeIndex > previousBeforeIndex && afterIndex > previousAfterIndex) {
      beforeIndex--;
      afterIndex--;
      result.push({type: 'same', beforeIndex, afterIndex});
    }

    if (beforeIndex === previousBeforeIndex) {
      afterIndex--;
      result.push({type: 'add', afterIndex});
    } else {
      beforeIndex--;
      result.push({type: 'delete', beforeIndex});
    }
  }

  while (beforeIndex > 0 && afterIndex > 0) {
    beforeIndex--;
    afterIndex--;
    result.push({type: 'same', beforeIndex, afterIndex});
  }

  while (beforeIndex > 0) {
    beforeIndex--;
    result.push({type: 'delete', beforeIndex});
  }

  while (afterIndex > 0) {
    afterIndex--;
    result.push({type: 'add', afterIndex});
  }

  return result.reverse();
};

/**
 * 2つの行配列をMyers法で比較し、行の対応付けを返す。
 */
const computeMyersOperations = (beforeLines: string[], afterLines: string[]): DiffOperation[] => {
  if (beforeLines.length === 0 && afterLines.length === 0) return [];

  const maxEditDistance = beforeLines.length + afterLines.length;
  let frontier = new Map<number, number>([[1, 0]]);
  const trace: Map<number, number>[] = [];

  for (let editDistance = 0; editDistance <= maxEditDistance; editDistance++) {
    const nextFrontier = new Map<number, number>();

    for (let diagonal = -editDistance; diagonal <= editDistance; diagonal += 2) {
      let beforeIndex: number;

      if (
        diagonal === -editDistance ||
        (diagonal !== editDistance &&
          getFrontierValue(frontier, diagonal - 1) < getFrontierValue(frontier, diagonal + 1))
      ) {
        // 下方向へ進む: after側の行を追加する。
        beforeIndex = getFrontierValue(frontier, diagonal + 1);
      } else {
        // 右方向へ進む: before側の行を削除する。
        beforeIndex = getFrontierValue(frontier, diagonal - 1) + 1;
      }

      let afterIndex = beforeIndex - diagonal;
      while (
        beforeIndex < beforeLines.length &&
        afterIndex < afterLines.length &&
        beforeLines[beforeIndex] === afterLines[afterIndex]
      ) {
        beforeIndex++;
        afterIndex++;
      }

      nextFrontier.set(diagonal, beforeIndex);

      if (beforeIndex >= beforeLines.length && afterIndex >= afterLines.length) {
        trace.push(nextFrontier);
        return backtrack(trace, beforeLines, afterLines);
      }
    }

    trace.push(nextFrontier);
    frontier = nextFrontier;
  }

  return [];
};

const normalizeLines = (
  lines: string[],
  normalizeForDiff?: NormalizeForDiff,
  options?: {includeVolatileState?: boolean}
): string[] => {
  if (!normalizeForDiff) return lines;

  try {
    const normalizedLines = normalizeForDiff(lines, options);
    return normalizedLines.length === lines.length ? normalizedLines : lines;
  } catch {
    return lines;
  }
};

/**
 * 原文の行番号・内容を保持しながら、必要に応じて別の比較用テキストで行差分を計算する。
 * 行の対応付けは、実行時I/O状態を除外した正規化結果に対するMyers法で行う。
 */
export const computeLineDiff = (
  before: string,
  after: string,
  normalizeForDiff?: NormalizeForDiff,
  options: ComputeLineDiffOptions = {}
): DiffLine[] => {
  const beforeLines = before ? before.split('\n') : [];
  const afterLines = after ? after.split('\n') : [];
  const ignoredBefore = countLeadingMatches(beforeLines, options.ignoreLeadingLinePattern);
  const ignoredAfter = countLeadingMatches(afterLines, options.ignoreLeadingLinePattern);
  const beforeComparisonLines = maskLeading(
    normalizeLines(beforeLines, normalizeForDiff, {includeVolatileState: false}),
    ignoredBefore
  )!;
  const afterComparisonLines = maskLeading(
    normalizeLines(afterLines, normalizeForDiff, {includeVolatileState: false}),
    ignoredAfter
  )!;

  // ハイライトだけを切り替えるための比較結果。行の対応付けには使わない。
  const volatileComparisonBefore = maskLeading(
    options.includeVolatileState
      ? normalizeLines(beforeLines, normalizeForDiff, {includeVolatileState: true})
      : null,
    ignoredBefore
  );
  const volatileComparisonAfter = maskLeading(
    options.includeVolatileState
      ? normalizeLines(afterLines, normalizeForDiff, {includeVolatileState: true})
      : null,
    ignoredAfter
  );
  // 比較用の値と表示する原文が異なりうるときは、左右それぞれの原文を返す
  const preserveSourceContents =
    normalizeForDiff !== undefined || options.ignoreLeadingLinePattern !== undefined;
  const includeVolatileComparison = normalizeForDiff !== undefined || options.includeVolatileState;

  return computeMyersOperations(beforeComparisonLines, afterComparisonLines).map(operation => {
    if (operation.type === 'same') {
      const beforeContent = beforeLines[operation.beforeIndex];
      const afterContent = afterLines[operation.afterIndex];
      const diffLine: DiffLine = {
        type: 'same',
        content: afterContent,
        beforeLineNum: operation.beforeIndex + 1,
        afterLineNum: operation.afterIndex + 1,
        ...(preserveSourceContents ? {beforeContent, afterContent} : {})
      };

      if (includeVolatileComparison) {
        diffLine.volatileStateChanged =
          volatileComparisonBefore !== null &&
          volatileComparisonAfter !== null &&
          volatileComparisonBefore[operation.beforeIndex] !==
            volatileComparisonAfter[operation.afterIndex];
      }

      return diffLine;
    }

    if (operation.type === 'delete') {
      const content = beforeLines[operation.beforeIndex];
      return {
        // 対応する相手のない先頭の除外行は、変更ではなく片側だけの行として返す
        type: operation.beforeIndex < ignoredBefore ? 'same' : 'delete',
        content,
        beforeLineNum: operation.beforeIndex + 1,
        ...(preserveSourceContents ? {beforeContent: content} : {})
      } satisfies DiffLine;
    }

    const content = afterLines[operation.afterIndex];
    return {
      type: operation.afterIndex < ignoredAfter ? 'same' : 'add',
      content,
      afterLineNum: operation.afterIndex + 1,
      ...(preserveSourceContents ? {afterContent: content} : {})
    } satisfies DiffLine;
  });
};
